// src/helpers/fetchSSE.js
// SSE stream reader for AG-UI events
// Matches auth encoding from existing api.js exactly

import { getLoggedInUser } from './authUtils';

const encodeToBase64 = (input) => btoa(input);

/**
 * POST to an SSE endpoint and return a Promise that resolves
 * with all parsed AG-UI events as an array.
 * Using Promise (not async generator) for Redux Saga compatibility.
 *
 * @param {string} url
 * @param {object} body
 * @param {function} onEvent - callback fired for each event as it arrives
 * @returns {Promise<array>} all events received
 */
export const fetchSSE = (url, body, onEvent = null) => {
  return new Promise((resolve, reject) => {
    const user = getLoggedInUser();
    const headers = { 'Content-Type': 'application/json' };

    // Add Authorization header if user is logged in, using same encoding as api.js
    // Exact same auth encoding as api.js
    if (user && user.id_token) {
      const callerLocationUrl =
        (window && window.location && window.location.href) || 'No Location Can Be found';
      const stringToEncode = `${callerLocationUrl}||${user.id_token}`;
      headers['Authorization'] = 'Bearer ' + encodeToBase64(stringToEncode);
    } else {
      console.warn('No user found; skipping Authorization header.');
    }

    fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok) {
          throw { status: response.status, description: `HTTP error ${response.status}` };
        }

        const reader  = response.body.getReader();
        const decoder = new TextDecoder();
        let   buffer  = '';
        const events  = [];

        function pump() {
          return reader.read().then(({ value, done }) => {
            if (done) {
              resolve(events);
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop(); // keep incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const event = JSON.parse(line.slice(6));
                  events.push(event);
                  // Fire callback immediately so saga can dispatch per-event
                  if (onEvent) onEvent(event);
                } catch {
                  // skip malformed lines
                }
              }
            }

            return pump(); // read next chunk
          });
        }

        return pump();
      })
      .catch((err) => {
        reject(err);
      });
  });
};
